import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { HoverHelpText } from "./HoverHelpText";
import { TextEditor } from "pymble-ui";
import "pymble-ui/styles.css";

const FIRST_REMINDER_DEFAULT = 30;
const SECOND_REMINDER_DEFAULT = 7;
const LOCK_DAYS_DEFAULT = 3;
const options: number[] = Array.from(Array(41).keys()).map((num) => num); //40 days 0-40

type ApprovalData = {
	Required: boolean;
	Checks: {
		EmergencyContact: boolean;
		Medical: boolean;
		Dietary?: boolean;
	};
	Compulsory: boolean;
	RSVP: {
		LockDays: number;
		ReminderDays: Array<number>;
	};
	TermsAndConditions: string;
	ExternalPermissionForm: string | null;
	ExternalMedicalForm: string | null;
};
interface EventPermissionProps {
	initialData: ApprovalData;
	onValueChange: (data: ApprovalData) => void;
}
export const EventPermissions: React.FC<EventPermissionProps> = ({
	initialData,
	onValueChange,
}) => {
	const [details, setDetails] = useState<ApprovalData>(initialData || {} as ApprovalData);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [permissionsChecked, setPermissionsChecked] = useState(
		details?.Required || false
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [compulsaryChecked, setCompulsaryChecked] = useState(
		details?.Compulsory || false
	);
	const [externalPermissionChecked, setExternalPermissionChecked] = useState(
		initialData?.ExternalPermissionForm ? true : false
	);

	const [lockDaysSelectValue, setLockDaysSelectValue] = useState(
		initialData?.RSVP?.LockDays || LOCK_DAYS_DEFAULT
	);
	const [firstReminderDaysSelectValue, setFirstReminderDaysSelectValue] =
		useState(
			initialData?.RSVP && initialData.RSVP.hasOwnProperty("ReminderDays")
				? initialData?.RSVP?.ReminderDays[0]
				: FIRST_REMINDER_DEFAULT
		);
	const [secondReminderDaysSelectValue, setSecondReminderDaysSelectValue] =
		useState(
			initialData?.RSVP && initialData.RSVP.hasOwnProperty("ReminderDays")
				? initialData?.RSVP?.ReminderDays[1]
				: SECOND_REMINDER_DEFAULT
		);
	const [medicalCheckValue, setMedicalCheckValue] = useState(
		initialData?.Checks?.Medical
	);

	const [
		externalMedicalPermissionChecked,
		setExternalMedicalPermissionChecked,
	] = useState(initialData?.ExternalMedicalForm ? true : false);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [medicalExternalURLValue, setMedicalExternalURLValue] = useState(
		initialData?.ExternalMedicalForm
	);

	const [emergencyContactCheckValue, setEmergencyContactCheckValue] = useState(
		initialData?.Checks?.EmergencyContact
	);

	const handleCheckChange =
		(key: "Medical" | "EmergencyContact" | "Dietary") =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { checked } = event.target;
			if (key === "Medical") setMedicalCheckValue(() => checked);
			if (key === "EmergencyContact") setEmergencyContactCheckValue(() => checked);
			setDetails((prevData) => ({
				...prevData,
				Checks: {
					...prevData.Checks,
					Medical: key === "Medical" ? checked : medicalCheckValue,
					EmergencyContact:
						key === "EmergencyContact" ? checked : emergencyContactCheckValue,
					Dietary: key === "Dietary" ? checked : prevData.Checks?.Dietary,
				},
			}));
		};
	const handleMedicalCheckChange = handleCheckChange("Medical");
	const handleEmergencyContactCheckChange = handleCheckChange("EmergencyContact");
	const handleDietaryCheckChange = handleCheckChange("Dietary");

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleTnCChange = (event: any) => {
		console.log("Event: ", event);
		setDetails((prevData) => ({
			...prevData,
			TermsAndConditions: event,
		}));
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { name, value, checked, type, id } = event.target;

		setDetails((prevData) => ({
			...prevData,
			[name]: type === "checkbox" || type === "radio" ? checked : value,
		}));
		if (name === "Required") {
			setPermissionsChecked(checked);
			// setMedicalCheckValue(checked);
			// setEmergencyContactCheckValue(checked);
		}
		if (name === "Compulsory") {
			setCompulsaryChecked(checked);
		}
		if (name === "external-permission") {
			setExternalPermissionChecked(checked);
			if (!checked) {
				setDetails((prevData) => ({
					...prevData,
					ExternalPermissionForm: null,
				}));
			}
		}

		if (name === "external-medical-permission") {
			setExternalMedicalPermissionChecked(checked);
			if (!checked) {
				setDetails((prevData) => ({
					...prevData,
					ExternalMedicalForm: null,
				}));
			}
		}
	};

	const handleFirstReminderDaysSelectChange = (
		event: React.ChangeEvent<HTMLSelectElement>
	) => {
		setFirstReminderDaysSelectValue((_prevValue) =>
			parseInt(event.target.value)
		);

		setDetails((prevData) => ({
			...prevData,
			RSVP: {
				LockDays: lockDaysSelectValue,
				ReminderDays: [
					parseInt(event.target.value),
					secondReminderDaysSelectValue,
				],
			},
		}));
	};

	const handleSecondReminderDaysSelectChange = (
		event: React.ChangeEvent<HTMLSelectElement>
	) => {
		setSecondReminderDaysSelectValue((_prevValue) =>
			parseInt(event.target.value)
		);

		setDetails((prevData) => ({
			...prevData,
			RSVP: {
				LockDays: lockDaysSelectValue,
				ReminderDays: [
					firstReminderDaysSelectValue,
					parseInt(event.target.value),
				],
			},
		}));
	};

	const handleLockDaySelectChange = (
		event: React.ChangeEvent<HTMLSelectElement>
	) => {
		setLockDaysSelectValue((_prevValue) => parseInt(event.target.value));
		setDetails((prevData) => ({
			...prevData,
			PermissionLockDays: parseInt(event.target.value),
			RSVP: {
				LockDays: parseInt(event.target.value),
				ReminderDays: [
					firstReminderDaysSelectValue,
					secondReminderDaysSelectValue,
				],
			},
		}));
	};

	useEffect(() => {
		onValueChange(details);
		setLockDaysSelectValue(details?.RSVP?.LockDays || LOCK_DAYS_DEFAULT);
		setFirstReminderDaysSelectValue(
			details?.RSVP?.ReminderDays
				? details?.RSVP?.ReminderDays[0]
				: FIRST_REMINDER_DEFAULT
		);
		setSecondReminderDaysSelectValue(
			details?.RSVP?.ReminderDays
				? details?.RSVP?.ReminderDays[1]
				: SECOND_REMINDER_DEFAULT
		);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [details]);

	return (
		<Container>
			<Row className="mb-3">
				<Col className="col-md-12">
					<h3>Permissions</h3>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col className="col-md-4">
					<Form.Check
						inline
						label="Permission required"
						type="checkbox"
						id="permissionReqd"
						name="Required"
						checked={details?.Required}
						onChange={handleChange}
					/>
					{details?.Required && (
						<>
							<Form.Check
								inline
								name="external-permission"
								label="External"
								checked={externalPermissionChecked}
								type="checkbox"
								onChange={handleChange}
							/>
							<HoverHelpText helpText="External contractor requires permission forms" />
							{externalPermissionChecked && (
								<>
									<Form.Label>
										External permission form
										<HoverHelpText helpText="Link to external permission form" />
									</Form.Label>

									<Form.Control
										type="text"
										placeholder="URL"
										name="ExternalPermissionForm"
										value={details?.ExternalPermissionForm ?? ""}
										onChange={handleChange}
									/>
								</>
							)}
						</>
					)}
				</Col>
				{details?.Required && (
					<Col className="col-md-4">
						<Form.Check
							inline
							label="Attendance is compulsory"
							type="checkbox"
							id="permissionComplsry"
							name="Compulsory"
							checked={details?.Compulsory}
							onChange={handleChange}
						/>
					</Col>
				)}
				{details?.Required && (
						<Col className="col-md-4">
							<Form.Check
								inline
								label="Medical update required"
								type="checkbox"
								id="medicalCheck"
								name="Medical"
								checked={details?.Checks?.Medical}
								onChange={handleMedicalCheckChange}
							/>
							{details?.Checks?.Medical && (
								<>
									<Form.Check
										inline
										name="external-medical-permission"
										label="External"
										checked={externalMedicalPermissionChecked}
										type="checkbox"
										onChange={handleChange}
									/>
									<HoverHelpText helpText="External contractor requires medical forms" />
									{externalMedicalPermissionChecked && (
										<>
											<br />
											<Form.Label>External medical form</Form.Label>
											<HoverHelpText helpText="Link to external medical form" />
											<Form.Control
												type="text"
												placeholder="URL"
												name="ExternalMedicalForm"
												value={details.ExternalMedicalForm ?? ""}
												onChange={handleChange}
											/>
										</>
									)}
								</>
							)}
						</Col>
				)}
			</Row>
			{details?.Required && (
				<Row className="mb-3">
					<Col className="col-md-4">
						<Form.Check
							inline
							label="Emergency contact update required"
							type="checkbox"
							id="emergencyCheck"
							name="EmergencyContact"
							checked={details?.Checks?.EmergencyContact}
							onChange={handleEmergencyContactCheckChange}
						/>
					</Col>
					<Col className="col-md-4">
						<Form.Check
							inline
							label="Dietary requirements required"
							type="checkbox"
							id="dietaryCheck"
							name="Dietary"
							checked={details?.Checks?.Dietary === true}
							onChange={handleDietaryCheckChange}
						/>
					</Col>
				</Row>
			)}
			{details?.Required && (
				<Row className="mb-3">
					<Col className="col-md-1">
						<Form.Label htmlFor="firstReminder">First Reminder</Form.Label>
					</Col>
					<Col className="col-md-1">
						<Form.Select
							id="firstReminder"
							name="FirstReminderDays"
							value={
								details?.RSVP?.ReminderDays &&
									details?.RSVP?.ReminderDays[0] > -1
									? details?.RSVP?.ReminderDays[0]
									: FIRST_REMINDER_DEFAULT
							}
							onChange={handleFirstReminderDaysSelectChange}
						>
							{options.map((option, index) => (
								<option key={index} value={option}>
									{option}
								</option>
							))}
						</Form.Select>
					</Col>
					<Col className="col-md-2">days before event</Col>
					<Col className="col-md-1">
						<Form.Label htmlFor="secondReminder">Second reminder</Form.Label>
					</Col>
					<Col className="col-md-1">
						<Form.Select
							id="secondReminder"
							value={
								details?.RSVP?.ReminderDays &&
									details?.RSVP?.ReminderDays[1] > -1
									? details?.RSVP.ReminderDays[1]
									: SECOND_REMINDER_DEFAULT
							}
							name="SecondReminderDays"
							onChange={handleSecondReminderDaysSelectChange}
						>
							{options.map((option, index) => (
								<option key={index} value={option}>
									{option}
								</option>
							))}
						</Form.Select>
					</Col>
					<Col className="col-md-2">days before event</Col>
					<Col className="col-md-1">
						<Form.Label htmlFor="permissionLockDays">RSVP by</Form.Label>
					</Col>
					<Col className="col-md-1">
						<Form.Select
							id="permissionLockDays"
							value={
								details?.RSVP?.LockDays > -1
									? details?.RSVP?.LockDays
									: LOCK_DAYS_DEFAULT
							}
							name="PermissionLockDays"
							onChange={handleLockDaySelectChange}
						>
							{options.map((option, index) => (
								<option key={index} value={option}>
									{option}
								</option>
							))}
						</Form.Select>
					</Col>
					<Col className="col-md-2">days before event</Col>
				</Row>
			)}
			{(details?.Required || details?.Compulsory) && (
				<Row className="mb-3">
					<Col className="col-md-12">
						<Form.Label className="form-check-label" htmlFor="tac_input">
							Terms &amp; Conditions
							{details?.Compulsory && (
								<span className="err-text"> *required</span>
							)}
						</Form.Label>
						<TextEditor
							content={details?.TermsAndConditions || ""}
							onUpdate={handleTnCChange}
						/>
						{/* <Form.Control
							as="textarea"
							rows={3}
							id="tac_input"
							className={
								(details?.Compulsory &&
									details?.TermsAndConditions &&
									details?.TermsAndConditions.length < 1 &&
									"err") ||
								""
							}
							name="TermsAndConditions"
							placeholder=""
							defaultValue={details?.TermsAndConditions}
							onChange={handleChange}
						/> */}
					</Col>
				</Row>
			)}
		</Container>
	);
	//}
};
